'use client';
import React, { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';

import { SignupCard, SignupIntro, SignupSectionHeader, SignupSplit } from '../components/SignupLayout';
import {
  enableSetHomeLocation,
  useNextSignupStep,
  usePreviousSignupStep,
  useSignupStep,
} from '@/app/(auth)/signup/components/signupStep/SignupStepContext';

import { useDebounce } from '@uidotdev/usehooks';
import { StepNavigation } from '@/app/(auth)/signup/components/StepNavigation';
import { StepError } from '@/app/(auth)/signup/components/StepError';
import { StepInput } from '@/app/(auth)/signup/components/StepInput';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { logrocketService } from '@/services/logrocket';
import { IEmailStatus } from '@/app/(auth)/signup/personal/isNewEmail/IEmailStatus';
import { IRouteErrorResponse } from '@/interfaces/IRouteErrorResponse';
import { routeErrorSchema } from '@/utils/RouteError';
import { isRouteErrorResponse } from '@/utils/isRouteErrorResponse';
import { StepNavigationLinks } from '../components/StepNavigationLinks';
import { useIsFixUp } from '../components/signupStep/useIsFixUp';
import { SelectHomeLocation } from '../components/SelectHomeLocation';
import { PasswordFields } from '../components/PasswordFields';
import { validatePassword } from '@/utils/helper/PasswordValidation';
import { Alert } from '@common/bw-ui/ui/alert';
import { Button } from '@common/bw-ui/ui/button';
import { FormField } from '@common/bw-ui/ui/form-field';
import { ArrowRightIcon } from '@common/bw-ui/icons';

export interface IPersonalDetailsProps {
  debounceDelay?: number;
}

export default function PersonalDetails(props: IPersonalDetailsProps) {
  const { details, setDetails, isSubmittingPrev, isPresent, fieldValidity, fieldErrors } = useSignupStep();
  const { handleSubmit: handleSubmitPrevious } = usePreviousSignupStep();

  useTransition();

  const router = useRouter();

  const { handleSubmit } = useNextSignupStep();

  const isFixup = useIsFixUp();
  // Once the Okta account exists the email and password are locked - on a Back-navigation they
  // can be viewed but not edited (fixup sessions keep their own read-only email treatment)
  const isLocked = !!details.oktaId && !details.isFixingUp;
  // Fixup users already have an account and never set a password here
  const needsPassword = !isLocked && !isFixup;

  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const passwordOk = password.length > 0 && validatePassword(password) && password === confirmPassword;

  const setHomeLocation = (homeLocation: number) => {
    setDetails({ ...details, homeLocation });
  };

  const debouncedEmail = useDebounce(
    details.email,
    props.debounceDelay === undefined ? 500 : props.debounceDelay,
  );
  const checkEmailQuery = useQuery({
    queryKey: ['checkEmail', { email: debouncedEmail }],
    queryFn: async () => {
      if (details.isFixingUp || isLocked) {
        return {
          exists: false,
          needsActivation: false,
        };
      }
      const query = new URLSearchParams({ email: debouncedEmail || '' });
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/signup/personal/isNewEmail?${query.toString()}`,
      );
      const result: IEmailStatus | IRouteErrorResponse = z
        .union([z.object({ exists: z.boolean(), needsActivation: z.boolean() }), routeErrorSchema])
        .parse(await res.json());

      if (isRouteErrorResponse(result)) {
        const error = new Error(result.error);
        logrocketService.logException(error);
        throw error;
      }

      return result;
    },

    enabled:
      debouncedEmail !== undefined &&
      debouncedEmail !== '' &&
      debouncedEmail.length > 3 &&
      fieldValidity['email'],
  });

  // Ignore email-check results once locked: the account exists by definition, and a cached
  // "needs activation" result can survive the client-side hop from the Activate button
  const emailIsTaken = !isLocked && checkEmailQuery.data && checkEmailQuery.data.exists;
  const emailNeedsActivation = !isLocked && checkEmailQuery.data && checkEmailQuery.data.needsActivation;
  const emailReady =
    isLocked || (checkEmailQuery.isSuccess && debouncedEmail === details.email && !emailIsTaken);

  // Locked emails never really check availability (the query short-circuits),
  // so don't surface a checking state for them
  const checkingEmail = !isLocked && checkEmailQuery.isLoading;

  let onActivateClicked = () => {
    const activateUrl = new URL(
      `${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/signup/personal/resumeVerification`,
    );
    activateUrl.searchParams.append('email', debouncedEmail || '');
    router.push(activateUrl.toString());
  };

  const backButton = (
    <Button
      id="previous"
      type="button"
      variant="outline"
      onClick={handleSubmitPrevious}
      disabled={isSubmittingPrev}
      className="tw:h-12 tw:rounded-[10px] tw:border-foreground tw:bg-white tw:px-5 tw:text-base tw:font-medium tw:text-foreground">
      Back
    </Button>
  );

  return (
    <SignupSplit>
      <SignupIntro
        headline={isFixup ? 'Confirm your details' : 'Create your account'}
        description={
          isFixup
            ? 'Please confirm your personal details.'
            : "Create an account and we'll remember your details, making it quicker to book and easier to manage everything next time."
        }
        steps={3}
        currentStep={1}
      />
      <form className="tw:group" data-testid="personalDetailsForm" onSubmit={handleSubmit}>
        <SignupCard>
          <StepError />

          {checkEmailQuery.error ? (
            <Alert variant="error" title="Something went wrong" description={checkEmailQuery.error.message} />
          ) : null}

          <SignupSectionHeader>Your Profile</SignupSectionHeader>

          <StepInput
            id="email"
            field="email"
            label="Email"
            description="Based in a Bruntwood building? Don't forget to use your work email address to enjoy discounts and stay connected with our community events!"
            placeholder="Enter your email address"
            type="email"
            readonly={details.isFixingUp || isLocked}
            isLoading={checkingEmail}
            // Green only once the uniqueness check has passed for the current value,
            // not merely when the format is valid
            success={emailReady}
            required
            overrideErrorMessage={
              // The needs-activation case has its own message + Activate button below -
              // showing the "please sign in" field error alongside it would contradict it
              emailIsTaken && !emailNeedsActivation
                ? 'This email is already in use. Please sign in or use a different email'
                : undefined
            }
            hasError={emailIsTaken || emailNeedsActivation}
          />

          {emailIsTaken && !emailNeedsActivation && (
            <>
              <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
                {backButton}
                <Button
                  id="emailTaken"
                  type="button"
                  variant="success"
                  onClick={e => {
                    e.preventDefault();
                    router.push('/login');
                  }}
                  className="tw:h-12 tw:flex-1 tw:rounded-[10px] tw:text-base tw:font-medium [&_svg]:tw:size-6">
                  Sign in
                  <ArrowRightIcon className="tw:size-6" aria-hidden="true" />
                </Button>
              </div>
              <StepNavigationLinks showSignIn={false} />
            </>
          )}

          {emailNeedsActivation && (
            <>
              <p className="tw:m-0 tw:text-sm tw:font-normal tw:text-destructive" role="alert">
                This email is already in use. Please activate your account.
              </p>
              <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
                {backButton}
                <Button
                  id="resendActivation"
                  type="button"
                  variant="success"
                  onClick={onActivateClicked}
                  className="tw:h-12 tw:flex-1 tw:rounded-[10px] tw:text-base tw:font-medium [&_svg]:tw:size-6">
                  Activate
                  <ArrowRightIcon className="tw:size-6" aria-hidden="true" />
                </Button>
              </div>
              <StepNavigationLinks showSignIn={false} />
            </>
          )}

          {!emailIsTaken && !emailNeedsActivation && (
            <>
              <StepInput
                id="name"
                label="Full Name"
                placeholder="First name last name"
                type="text"
                field="name"
                required
              />
              <StepInput
                id="phone"
                label="Phone Number"
                placeholder="e.g. +44 123 456 7890"
                type="tel"
                required
                field="phone"
              />

              {enableSetHomeLocation && (
                <FormField
                  id="homeLocation"
                  label="Select your home location"
                  requirement="required"
                  description="If you are not based in a Bruntwood building please choose a location based on where you think you will spend the most time. Don't worry this can always be changed later!"
                  error={isPresent ? fieldErrors['homeLocation']?.join(' ') : undefined}>
                  <SelectHomeLocation
                    homeLocation={details.homeLocation || 0}
                    setHomeLocation={setHomeLocation}
                  />
                </FormField>
              )}

              {!isFixup && (
                <>
                  <SignupSectionHeader>Your Password</SignupSectionHeader>
                  {isLocked ? (
                    <p
                      className="tw:m-0 tw:text-sm tw:font-normal tw:text-foreground"
                      data-testid="passwordAlreadySet">
                      Your password has been set.
                    </p>
                  ) : (
                    <PasswordFields
                      password={password}
                      confirmPassword={confirmPassword}
                      setPassword={setPassword}
                      setConfirmPassword={setConfirmPassword}
                    />
                  )}
                </>
              )}

              <StepNavigation
                preventNext={!emailReady || (needsPassword && !passwordOk)}
                extra={needsPassword ? { password, confirmPassword } : undefined}
              />
            </>
          )}
        </SignupCard>
      </form>
    </SignupSplit>
  );
}
